import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'
import useFormal from '@kevinwolf/formal-web'
import Grid from '@material-ui/core/Grid'
import { useTranslation } from 'react-i18next'
import { SubmitButton } from 'components/elements/SubmitButton'
import { Typography } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import { SelectField } from 'components/elements/SelectField'
import { ISpecialRatesZonesZoneData, useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { useSpecialRates } from '../SpecialRatesProvider'
import { useHistory } from 'react-router-dom'
import Button from '@material-ui/core/Button'
import { useClasses } from '../SpecialRates.css'
import { Autocomplete } from '@material-ui/lab'
import TextField from '@material-ui/core/TextField'
import { normalizePlate } from 'utils'
import debounce from 'debounce'
import HelperTextSuccessIcon from '@material-ui/icons/CheckCircleOutline'
import HelperTextWarningIcon from '@material-ui/icons/ErrorOutline'

export type Props = {}
export type Functions = {}
export type AllProps = Props & Functions

const Toolbar: FunctionComponent<AllProps> = ({}) => {
  const { t, i18n } = useTranslation()
  const api = useApi()
  const history = useHistory()
  const classes = useClasses()

  const { showSuccess, showError } = useMessage()
  const { loadRates } = useSpecialRates()

  const [zones, setZones] = useState<ISpecialRatesZonesZoneData[]>([])
  const [plateNumberFieldOptions, setPlateNumberFieldOptions] = useState<string[]>([])
  const [plateNumberStatus, setPlateNumberStatus] = useState<'idle' | 'found' | 'not-found'>('idle')

  const plateNumberFieldHelperText = useMemo(() => {
    switch (plateNumberStatus) {
      case 'found':
        return <>
          <HelperTextSuccessIcon fontSize="small" className={`${classes.plateNumberHelperIcon} ${classes.plateNumberHelperIconSuccess}`} />
          {t('specialRates.plateNumberFoundHelpText')}
        </>
      case 'not-found':
        return <>
          <HelperTextWarningIcon fontSize="small" className={`${classes.plateNumberHelperIcon} ${classes.plateNumberHelperIconWarning}`} />
          {t('specialRates.plateNumberNotFoundHelpText')}
        </>
      default:
        return t('specialRates.plateNumberHelpText')
    }
  }, [plateNumberStatus, t, classes])

  useEffect(() => {
    api.getSpecialRatesZones()
      .then(data => {
        setZones(data.zones)
      })
      .catch(() => {
      })
  }, [])

  useEffect(() => {
    if (zones.length == 1) {
      formal.change('zone', zones[0].id)
    }
  }, [zones])

  const initialValues = {
    plateNumber: '',
    zone: 0,
    offer: null,
  }

  const schema = yup.object().shape({
    plateNumber: yup.string().required().min(2),
    zone: yup.number().required().min(1),
    offer: yup.number().required(),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      await api.assignSpecialRate({
        plate_number: values.plateNumber.toUpperCase(),
        offer_id: values.offer,
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          formal.change('plateNumber', '')

          loadRates()

          showSuccess(t('messages.freeTimeAssigned'))
        })
        .catch(error => {
          if (error.status == 401) {
            history.push('/')

            return
          }

          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            case 100:
              message = t('alerts.parkingSessionNotFound')
              break
            case 102:
              message = t('alerts.maxActiveSessionsReached', { limit: error.extra.limit })
              break
            case 103:
              message = t('alerts.freeTimeAlreadyAssigned')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotAssignFreeTime')} ${message}`)
        })
    },
  })

  useEffect(() => {
    const offers = (zones.find(z => z.id == formal.values.zone) || {}).offers || []

    formal.change('offer', offers.length ? offers[0].id : null)
  }, [formal.values.zone])

  const fetch = useMemo(
    () =>
      debounce((params: { zone_id: number, plate_number: string }, callback: (plate_numbers: string[]) => void) => {
        api.getSpecialRatesPlateNumbers(params)
          .then(data => {
            callback(data.plate_numbers)

            if (data.plate_numbers.some(plate_number => plate_number == params.plate_number)) {
              setPlateNumberStatus('found')
            } else {
              setPlateNumberStatus('not-found')
            }
          })
          .catch(() => {
          })
      }, 500),
    [],
  )

  useEffect(() => {
    let mounted = true

    if (formal.values.plateNumber == '' || !formal.values.zone) {
      fetch.clear()
      setPlateNumberFieldOptions([])
      setPlateNumberStatus('idle')

      return
    }

    fetch({ zone_id: formal.values.zone, plate_number: formal.values.plateNumber }, (plate_numbers: string[]) => {
      if (mounted) {
        const sorted = plate_numbers.sort((x, y) => {
          const xStarts = x.startsWith(formal.values.plateNumber)
          const yStarts = y.startsWith(formal.values.plateNumber)

          if (xStarts && !yStarts) return -1
          if (!xStarts && yStarts) return 1

          return x.localeCompare(y)
        })

        setPlateNumberFieldOptions(sorted)
      }
    })

    return () => {
      mounted = false
    }
  }, [formal.values.plateNumber, formal.values.zone, fetch])

  return (
    <Box className={classes.toolbarContainer}>
      <form {...formal.getFormProps()}>
        <Typography color="textSecondary" gutterBottom style={{ fontSize: 14, marginBottom: 14 }}>
          {t('specialRates.inputDataToAssignRate')}
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm className={classes.plateNumberFieldWrapper}>
            <Autocomplete
              value={null}
              inputValue={formal.values.plateNumber}
              onInputChange={(event, value) => {
                const normalizedValue = normalizePlate(value)

                formal.change('plateNumber', normalizedValue)

                if (value.toUpperCase() == normalizedValue) {
                  return
                }

                const target = event.target as HTMLInputElement
                const valueLengthDiff = normalizedValue.length - value.length
                const selectionStart = target.selectionStart + valueLengthDiff
                const selectionEnd = target.selectionEnd + valueLengthDiff

                setTimeout(target => {
                  target.selectionStart = selectionStart
                  target.selectionEnd = selectionEnd
                }, 0, target)
              }}
              options={plateNumberFieldOptions}
              freeSolo
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('plateNumber')}
                  variant="outlined"
                  fullWidth
                  autoFocus
                  inputProps={{
                    ...params.inputProps,
                    maxLength: 10,
                    style: {
                      textTransform: 'uppercase',
                      fontFamily: '"Roboto Mono", monospace',
                      lineHeight: '18px',
                    },
                  }}
                  error={!!formal.errors.plateNumber}
                  helperText={plateNumberFieldHelperText}
                />
              )}
            />
          </Grid>
          <SubmitButton
            buttonText={t('assign')}
            style={{ minHeight: 56 }}
            disabled={formal.isSubmitting}
          />
        </Grid>
        <Grid container spacing={1} style={{ marginTop: 16 }}>
          <SelectField
            GridProps={{ xs: 12, sm: true, className: classes.zoneFieldWrapper }}
            label={t('zone')}
            optionsCustom={[
              {
                label: zones.length > 1 ? t('select') : '',
                id: 0,
              },
              ...zones.sort((a, b) => a.code.localeCompare(b.code)).map(zone => {
                const localization = (zone.localizations || []).find(l => l.language == i18n.language)

                return {
                  label: `${zone.code}${localization && localization.name ? ` ${localization.name}` : ''}`,
                  id: zone.id,
                }
              }),
            ]}
            {...formal.getFieldProps('zone')}
            disabled={zones.length == 1 || formal.isSubmitting}
          />
          {((zones.find(z => z.id == formal.values.zone) || {}).offers || []).map((offer, key) => (
            <Grid item key={key}>
              <Button
                variant={formal.values.offer == offer.id ? 'contained' : 'outlined'}
                size="large"
                color="secondary"
                style={{ minHeight: 56 }}
                onClick={() => formal.change('offer', offer.id)}
              >
                {offer.name}
              </Button>
            </Grid>
          ))}
        </Grid>
      </form>
    </Box>
  )
}

export default Toolbar
